import type { ProductConfigBuilder } from "../product-types";
import { DECO, breadcrumb } from "./shared";
import heroLifestyle from "@/assets/products/tv6/hero-lifestyle.jpg";
import performanceOpaque from "@/assets/products/tv6-ultra/performance-opaque.jpg";
import performanceTransparent from "@/assets/products/tv6/performance-transparent.jpg";
import ports from "@/assets/products/tv6/ports-closeup.jpg";
import remote from "@/assets/products/tv6/remote.jpg";
import softwareLifestyle from "@/assets/products/tv6-ultra/software-lifestyle-clean.jpg";

export const buildTv6Config: ProductConfigBuilder = (ctx) => {
  const m = ctx.messages;
  const u = ctx.urls;

  return {
    breadcrumb: breadcrumb(ctx, "inext TV6"),
    sections: [
      {
        kind: "hero",
        bgClassName: "bg-muted/10",
        decorations: [{ kind: "grid", className: DECO.grid25 }],
        primaryGlow: false,
        eyebrow: m.tv6HeroEyebrow,
        headline: m.tv6HeroHeadline,
        sub: m.tv6HeroSub,
        contactHref: u.contactSales,
        contactLabel: m.tv6UltraContactSales,
        distributorHref: u.distributors,
        distributorLabel: m.tv6UltraFindDistributor,
        compareHref: u.compare,
        compareLabel: m.tv6CompareLabel,
        media: {
          type: "image",
          src: heroLifestyle,
          alt: m.tv6HeroImageAlt,
          width: 1200,
          height: 800,
        },
      },
      {
        kind: "glance",
        bgClassName: "bg-card/50",
        stats: [
          [m.tv6GlanceValue1, m.tv6GlanceLabel1],
          [m.tv6GlanceValue2, m.tv6GlanceLabel2],
          [m.tv6GlanceValue3, m.tv6GlanceLabel3],
          [m.tv6GlanceValue4, m.tv6GlanceLabel4],
        ],
      },
      {
        kind: "intro",
        name: "TV6",
        paras: [m.tv6IntroPara1, m.tv6IntroPara2],
      },
      {
        kind: "software",
        bgClassName: "bg-muted/20",
        layout: "imageLeft",
        eyebrow: m.tv6UltraV2SoftwareEyebrow,
        title: m.tv6SoftwareTitle,
        body: m.tv6SoftwareBody,
        cards: [
          [m.tv6UltraV2SoftwareCustomTitle1, m.tv6UltraV2SoftwareCustomBody1],
          [m.tv6UltraV2SoftwareCustomTitle2, m.tv6UltraV2SoftwareCustomBody2],
          [m.tv6UltraV2SoftwareCustomTitle3, m.tv6UltraV2SoftwareCustomBody3],
        ],
        media: {
          type: "image",
          src: softwareLifestyle,
          alt: m.productSoftwareLifestyleImageAlt,
          width: 1200,
          height: 675,
          loading: "lazy",
        },
      },
      {
        kind: "connectivity",
        decorations: [{ kind: "grid", className: DECO.grid20 }],
        layout: "imageRight",
        eyebrow: m.tv6UltraV2ConnectivityEyebrow,
        title: m.tv6ConnectivityTitle,
        caption: { style: "list", value: m.tv6ConnectivityCaption },
        media: {
          type: "image",
          src: ports,
          alt: m.tv6PortsImageAlt,
          width: 1200,
          height: 799,
          loading: "lazy",
        },
      },
      {
        kind: "performance",
        bgClassName: "bg-muted/10",
        layout: "imageLeft",
        eyebrow: m.tv6UltraV2PerformanceEyebrow,
        title: m.tv6PerformanceTitle,
        body: m.tv6PerformanceBody,
        accent: { style: "chips", value: m.tv6PerformanceAccent },
        media: {
          type: "reveal",
          opaque: performanceOpaque,
          transparent: performanceTransparent,
          width: 1200,
          height: 800,
          label: m.tv6HardwareRevealLabel,
          alt: m.tv6HardwareRevealImageAlt,
        },
      },
      {
        kind: "remote",
        decorations: [{ kind: "primaryBlob", className: DECO.remotePrimaryBlob }],
        layout: "imageRight",
        eyebrow: m.tv6RemoteEyebrow,
        title: m.tv6RemoteTitle,
        body: m.tv6RemoteBody,
        bullets: [m.tv6RemoteFeature1, m.tv6RemoteFeature2, m.tv6RemoteFeature3],
        appLink: { href: u.remoteApp, label: m.remoteAppProductCallout },
        note: {
          title: m.tv6RemoteBtNoteTitle,
          body: m.tv6RemoteBtNoteBody,
          link: {
            href: `${u.accessories}?category=remotes#accessory-list`,
            label: m.tv6RemoteBtNoteLink,
          },
        },
        media: {
          type: "image",
          src: remote,
          alt: m.tv6RemoteImageAlt,
          width: 701,
          height: 561,
          loading: "lazy",
        },
      },
      {
        kind: "alcatraz",
        pill: m.operationsStatPill,
        title: m.tv6UltraAlcatrazTitle,
        body: m.tv6AlcatrazBody,
        ctaHref: u.alcatraz,
        ctaLabel: m.tv6UltraAlcatrazCta,
      },
      {
        kind: "specs",
        bgClassName: "bg-muted/10",
        title: m.tv6UltraV2SpecsTitle,
        note: m.packageNote,
        groups: [
          {
            title: m.tv6UltraV2SpecsGroupHardware,
            rows: [
              [m.tv6UltraSpecChipset, "Allwinner H618"],
              [m.tv6UltraSpecCpu, m.productSpecCpuQuadCoreArm],
              [m.tv6UltraSpecGpu, "ARM Mali-G31 MP2"],
              [m.tv6UltraSpecRam, "2 GB DDR3"],
              [m.tv6UltraSpecStorage, "16 GB eMMC"],
              [m.tv6UltraSpecDimensions, "103 × 103 × 20 mm"],
              [m.tv6UltraSpecPower, "5 V / 2 A"],
            ],
          },
          {
            title: m.tv6UltraV2SpecsGroupConnectivity,
            rows: [
              [m.tv6UltraSpecHdmi, m.productSpecHdmiCableIncluded],
              [m.tv6UltraSpecEthernet, m.productSpecEthernet100],
              [m.tv6UltraSpecWifi, m.productSpecWifiDualBandAc],
              [m.tv6UltraSpecBluetooth, "Bluetooth 5.0"],
              [m.tv6UltraSpecUsb, m.productSpecUsbHost],
              [m.tv6UltraSpecCardReader, "microSD / TF"],
              [m.tv6UltraSpecAudioOut, m.productSpecAudioSpdif],
              [m.tv6IrAvPortLabel, m.tv6IrAvPortSpec],
            ],
          },
          {
            title: m.tv6UltraV2SpecsGroupMedia,
            rows: [
              [m.tv6UltraSpecMaxRes, m.tv6UltraSpecMaxResValue],
              [
                m.tv6UltraSpecVideoCodecs,
                "H.265 (4K@60) · VP9 P2 (4K@30) · H.264 (4K@30) · MPEG-1/2/4 · AVS · WMV9 · VP8 · Xvid/DivX",
              ],
              [
                m.tv6UltraSpecVideoFormats,
                "AVI · MKV · ISO · VOB · DAT · MPG/MPEG · MP4 · MOV · TS · XVID · ASF · FLV · WMV",
              ],
              [
                m.tv6UltraSpecAudioFormats,
                "MP3 · WMA · AAC · WAV · OGG · MIDI · FLAC · APE · ALAC · M4A",
              ],
              [m.tv6UltraSpecImageFormats, "JPG/JPEG · HD JPEG · TIFF · PNG · BMP · GIF"],
              [m.tv6UltraSpec3d, m.tv6UltraSpec3dValue],
            ],
          },
          {
            title: m.tv6UltraV2SpecsGroupSoftware,
            rows: [
              [m.tv6UltraSpecOs, "Android 12 (AOSP)"],
              [m.tv6UltraSpecDms, "inext Alcatraz™ DMS"],
              [m.tv6UltraSpecTr069, m.tv6UltraSpecTr069Yes],
              [m.tv6UltraSpecFilesystems, "exFAT · FAT32 · NTFS · EXT · HFS+"],
              [m.tv6UltraSpecLanguages, m.tv6UltraSpecLanguagesValue],
              [m.tv6UltraSpecInternet, m.productSpecInternetFeatures],
            ],
          },
          {
            title: m.tv6UltraV2SpecsGroupPackage,
            rows: [
              ["·", m.tv6PackageRemote],
              ["·", m.tv6UltraPackagePower],
              ["·", m.tv6UltraPackageHdmi],
              ["·", m.tv6UltraPackageBatteries],
              ["·", m.tv6UltraPackageGuide],
            ],
          },
        ],
      },
      {
        kind: "cta",
        title: m.tv6CtaTitle,
        body: m.tv6CtaBody,
        compareHref: u.compare,
        compareLabel: m.tv6CompareLabel,
        contactHref: u.contactSales,
        contactLabel: m.tv6UltraContactSales,
        distributorHref: u.distributors,
        distributorLabel: m.tv6UltraFindDistributor,
      },
    ],
  };
};
